<template>
  <view-wrapper
    :show-back-button="true"
    name="QualityAssurance - Approval view"
    path="qa/labels"
    back-text="Back to labels list"
    :loading="loading"
    loading-text="Loading QualityAssuranceLabelDetails">

    <div class="float-right font-semibold">
      <span v-if="!!message">
        {{message}}
      </span>

    </div>

    <wrapper class="mb-3 p-5" >
      <label-comparisons :data="preview" :printers="printers" />
    </wrapper>

    <wrapper class="mb-3 p-5" >
      <label-variables-delta :data="preview" />
    </wrapper>

    <wrapper class="mb-3 p-5">
      <p v-if="availablePermissions.length > 0" class="mt-5">Approvers</p>

      <authorization-zone allowAny="QualityAssuranceRole:DesignApprover">
        <div class="pt-2 flex">
          <checkbox class="m-1" :value="permissions.includes('DesignApprover')" @input="addRemoveApprover('DesignApprover')" />
          <div class="cursor-pointer select-none" @click="addRemoveApprover('DesignApprover')">
            <span class="block pl-5">Design approver</span>
            <span class="block pl-5 text-xs">Approve the changes as Design Approver</span>
          </div>
        </div>
      </authorization-zone>
      
      <authorization-zone allowAny="QualityAssuranceRole:DataApprover">
        <div class="pt-2 flex">
          <checkbox class="m-1" :value="permissions.includes('DataApprover')" @input="addRemoveApprover('DataApprover')" />
          <div class="cursor-pointer select-none" @click="addRemoveApprover('DataApprover')">
            <span class="block pl-5">Data approver</span>
            <span class="block pl-5 text-xs">Approve the changes as Data Approver</span>
          </div>
        </div>
      </authorization-zone>
    </wrapper>

    <authorization-zone allowAny="QualityAssuranceRole:DataApprover,QualityAssuranceRole:DesignApprover">
      <grafokett-button class="big ml-2 bg-primary text-textcolor" @click="modalApprove = !modalApprove" :disabled="permissions.length === 0">APPROVE</grafokett-button>
      <grafokett-button class="big ml-2 bg-primary text-textcolor" @click="modalReject = !modalReject" :disabled="permissions.length === 0">REJECT</grafokett-button>
    </authorization-zone>

    <modal
    v-if="modalApprove"
    :type="'primary'"
    @close="modalApprove = !modalApprove"
    >
      <template slot="body" >
        <div class="p-2 my-2" >
          <comment-box placeholderText="Describe changes to approve..." v-model="comment" />
        </div>
      </template>
      <template slot="buttons" >
        <div>
          <grafokett-button
            type="primary"
            class="big bg-white border-primary text-primary"
            @click="onConfirmApprove"
          >Approve</grafokett-button>
          <grafokett-button
            class="big ml-2 bg-primary text-textcolor"
            @click="modalApprove = !modalApprove"
          >Go back</grafokett-button
          >
        </div>
      </template>
    </modal>

    <modal
    v-if="modalReject"
    :type="'warning'"
    @close="modalReject = !modalReject"
    :heading="'Reject'"
    :description="'Reject changes with comment.'"
    >
      <template slot="body" >
        <div class="p-2 my-2" >
          <comment-box placeholderText="Describe changes being rejected..." v-model="comment" />
        </div>
      </template>
      <template slot="buttons" >
        <div>
          <grafokett-button
            class="big bg-white border-primary text-primary"
            @click="onConfirmReject"
          >Yes, reject</grafokett-button
          >
          <grafokett-button
            class="big ml-2 bg-primary text-textcolor"
            @click="modalReject = !modalReject"
          >Go back</grafokett-button
          >
        </div>
      </template>
    </modal>

    <modal
    v-if="approvalSuccessful"
    :type="'success'"
    :heading="'Changes have been approved!'"
    @close="approvalSuccessful = !approvalSuccessful"
    >
      <template slot="buttons" >
        <div>
          <grafokett-button
            class="big bg-white border-primary text-primary"
            @click="goToPublish"
          >Go to publish</grafokett-button
          >
          <grafokett-button
            class="big ml-2 bg-primary text-textcolor"
            @click="goBack"
          >Go back to details</grafokett-button
          >
        </div>
      </template>
    </modal>

    <modal
    v-if="rejectSuccessful"
    :type="'success'"
    :heading="'Changes have been rejected!'"
    @close="rejectSuccessful = !rejectSuccessful"
    >
      <template slot="buttons" >
        <div>
          <grafokett-button
            class="big ml-2 bg-primary text-textcolor"
            @click="goBack"
          >Go back to details</grafokett-button
          >
        </div>
      </template>
    </modal>

  </view-wrapper>


</template>

<script>

import GrafokettButton from '../../../components/forms/Button.vue'
import LabelComparisons from "components/LabelComparisons";
import LabelVariablesDelta from "components/LabelVariablesDelta";
import Checkbox from "../../../components/forms/Checkbox";
import Modal from "components/Modal";
import CommentBox from "components/CommentBox";
import AuthorizationZone from "components/AuthorizationZone";

export default {
  components: {
    AuthorizationZone,
    CommentBox,
    GrafokettButton,
    LabelComparisons,
    LabelVariablesDelta,
    Checkbox,
    Modal
  },
  data: () => ({
    loading: false,
    modalApprove: false,
    modalReject: false,
    message: "",
    comment: "",
    preview: [],
    permissions: [],
    approvalSuccessful: false,
    rejectSuccessful: false,
    label: null
  }),
  computed: {
    title () {
      return 'QualityAssuranceLabelDetails'
    },
    availablePermissions() {
      return this.$store.getters['QUALITY_ASSURANCE/ROLES'].filter(r => r.qualityAssuranceRole === 'DataApprover' || r.qualityAssuranceRole === 'DesignApprover')
    },
    printers () {
      return this.$store.getters['PRINTERS/INSTALLED_PRINTERS'];
    }
  },
  methods: {
    async getData () {
      this.loading = true;
      await this.$store.dispatch('QUALITY_ASSURANCE/GET_ROLES');
      await this.$store.dispatch('QUALITY_ASSURANCE/GET_PREVIEW', this.$route.params);
      this.preview = [this.$store.getters['QUALITY_ASSURANCE/PREVIEW']].map(d => ({
        labelId: this.$route.params.labelId,
        versionId: this.$route.params.versionId,
        quantity: 1,
        name: this.label.name,
        variablesDelta: d.variablesDelta,
        diff: {
          preview: d.design.diffPng == null ?  '' : "data:image/png;base64, " + d.design.diffPng
        },
        left: {
          variables: d.variables.leftVariables,
          preview: "data:image/png;base64, " + d.design.leftPng
        },
        right: {
          variables: d.variables.rightVariables,
          preview: d.design.rightPng == null ?  '' : "data:image/png;base64, " + d.design.rightPng
        }
      }));
      this.loading = false;
    },
    sortVariables(left, right) {
      return left.filter(l => !right.some(r => (r.name === l.name)))
    },
    addRemoveApprover (id) {
      const i = this.permissions.indexOf(id);
      if (i === -1) this.permissions.push(id);
      else this.permissions.splice(i, 1);
    },
    gotoPublish(labelId) {
      this.$router.push(`/qa/labels/${labelId}/publish`);
    },
    async onConfirmReject() {
      try {
        this.loading = true
        const success = await this.sendApprovals('Rejected', this.permissions)
        if(success) {
          this.modalReject = !this.modalReject
          this.rejectSuccessful = true
          this.comment = ''
        }
        this.loading = false
      } catch(error) {
        this.loading = false
        this.rejectSuccessful = false
        throw error
      }
    },
    async onConfirmApprove() {
      try {
        this.loading = true
        const success = await this.sendApprovals('Approved', this.permissions)
        if(success) {
          this.modalApprove = !this.modalApprove
          this.approvalSuccessful = true
          this.comment = ''
        }
        this.loading = false
      } catch(error) {
        this.loading = false
        this.approvalSuccessful = false
        throw error
      }
    },
    async sendApprovals(status, approverRoles) {
      try {
        for(var roleIndex in approverRoles) {
          const payload = {
            labelId: this.$route.params.labelId,
            labelVersionId: this.$route.params.versionId,
            comment: this.comment,
            approverRole: approverRoles[roleIndex],
            approvalStatus: status
          }
          const response = await this.$store.dispatch('QUALITY_ASSURANCE/APPROVALS', payload)
          if(!response.data.success) {
            return false;
          }
        }
        return true;
      } catch(error) {
        throw error
      }
    },
    goToPublish() {
      this.$router.push(`/qa/labels/${this.$route.params.labelId}/versions/${this.$route.params.versionId}/publish`)
    },
    goBack() {
      this.$router.push(`/qa/labels/${this.$route.params.labelId}/details`)
    }
  },
  async mounted () {
    if (this.printers.length === 0) {
      await this.$store.dispatch('PRINTERS/GET_INSTALLED_PRINTERS');
    }
    if(!this.label) {
      const response = await this.$store.$api.get(`/api/labels/${this.$route.params.labelId}`);
      this.label = { ...response.data.data };
    }
    await this.getData();
  }
}
</script>
